import { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { Input } from "@/components/ui/input";
import { ArrowLeft, Users, Search, Mail, ShieldCheck, ShoppingBag, ShoppingCart, Zap, Clock, Star, X, Globe, Eye, Plus, Minus } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }); }
  catch { return iso.slice(0, 10); }
}

function fmtDateTime(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(undefined, { year: "numeric", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); }
  catch { return iso.slice(0, 16); }
}

function UserDetail({ userId, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axios.get(`${API}/api/admin/users/${userId}`)
      .then(res => { if (!cancelled) { setData(res.data); setLoading(false); } })
      .catch(e => { if (!cancelled) { setError(e.response?.data?.detail || "Failed to load user"); setLoading(false); } });
    return () => { cancelled = true; };
  }, [userId]);

  return (
    <div className="fixed inset-0 z-50 bg-[#050505]/90 flex items-start justify-center overflow-y-auto p-4 sm:p-8" data-testid="user-detail-modal">
      <div className="bg-[#0D0D0D] border border-[#39FF14]/30 max-w-3xl w-full p-6 sm:p-8 my-4 relative">
        <button onClick={onClose} data-testid="user-detail-close" className="absolute top-4 right-4 text-[#A1A1A1] hover:text-[#39FF14]">
          <X className="h-5 w-5" />
        </button>
        {loading && <div className="text-[#A1A1A1] font-mono text-sm">Loading user…</div>}
        {error && <div className="text-red-400 font-mono text-sm">{error}</div>}
        {data && (
          <div className="space-y-6">
            <div>
              <p className="font-mono uppercase tracking-[0.3em] text-xs font-bold text-[#39FF14] mb-2">User</p>
              <h2 className="text-2xl sm:text-3xl font-black tracking-tight text-[#F2F2F2] break-all" style={{ fontFamily: "Chivo, sans-serif" }}>
                {data.user.name || "(no name)"}
              </h2>
              <p className="text-[#A1A1A1] font-mono text-sm break-all">{data.user.email}</p>
              <p className="font-mono text-xs mt-2" data-testid="detail-referral">
                <span className="text-[#A1A1A1] uppercase tracking-wider">Where they heard about us: </span>
                <span className={data.user.referral_source ? "text-[#39FF14] font-bold" : "text-[#A1A1A1]/60"}>
                  {data.user.referral_source || "Not specified"}
                </span>
              </p>
              <div className="flex flex-wrap gap-2 mt-3 text-[10px] font-mono uppercase tracking-wider">
                <span className={`px-2 py-1 ${data.user.role === "admin" ? "bg-[#39FF14] text-[#050505]" : "bg-[#1A1A1A] text-[#A1A1A1]"}`}>{data.user.role}</span>
                <span className={`px-2 py-1 ${data.user.email_verified ? "bg-[#39FF14]/10 text-[#39FF14]" : "bg-yellow-500/10 text-yellow-400"}`}>
                  {data.user.email_verified ? "Email verified" : "Unverified"}
                </span>
                <span className="px-2 py-1 bg-[#1A1A1A] text-[#A1A1A1]">Joined {fmtDate(data.user.created_at)}</span>
              </div>
            </div>

            <Section title={`Orders (${data.orders.length})`} icon={ShoppingBag}>
              {data.orders.length === 0 ? <Empty>No orders</Empty> : data.orders.map(o => (
                <div key={o.id} className="flex justify-between items-center py-2 border-b border-[#1A1A1A] last:border-0 text-sm" data-testid={`detail-order-${o.id}`}>
                  <div>
                    <span className="font-mono text-[#F2F2F2]">#{(o.id || "").slice(-8)}</span>
                    <span className="font-mono text-[10px] text-[#A1A1A1] uppercase tracking-wider ml-3">{o.status}</span>
                  </div>
                  <div className="text-right">
                    <p className="font-mono text-[#39FF14] font-bold">${Number(o.total || 0).toFixed(2)}</p>
                    <p className="font-mono text-[10px] text-[#A1A1A1]">{fmtDate(o.created_at)}</p>
                  </div>
                </div>
              ))}
            </Section>

            <Section title={`Preorders (${data.preorders.length})`} icon={Zap}>
              {data.preorders.length === 0 ? <Empty>No preorders</Empty> : data.preorders.map(p => (
                <div key={p.id} className="flex justify-between items-center py-2 border-b border-[#1A1A1A] last:border-0 text-sm" data-testid={`detail-preorder-${p.id}`}>
                  <div>
                    <span className="font-mono text-[#F2F2F2]">#{(p.id || "").slice(-8)}</span>
                    <span className="font-mono text-[10px] text-[#A1A1A1] uppercase tracking-wider ml-3">{p.status}</span>
                  </div>
                  <div className="text-right">
                    <p className="font-mono text-[#39FF14] font-bold">${Number(p.amount || 0).toFixed(2)}</p>
                    <p className="font-mono text-[10px] text-[#A1A1A1]">{fmtDate(p.created_at)}</p>
                  </div>
                </div>
              ))}
            </Section>

            <Section title={`Waitlist (${data.waitlist.length})`} icon={Clock}>
              {data.waitlist.length === 0 ? <Empty>Not on any waitlists</Empty> : data.waitlist.map(w => (
                <div key={w.id} className="flex justify-between items-center py-2 border-b border-[#1A1A1A] last:border-0 text-sm">
                  <span className="text-[#F2F2F2]">{w.product_name || w.category || w.product_id}</span>
                  <span className="font-mono text-[10px] text-[#A1A1A1]">{fmtDate(w.created_at)}</span>
                </div>
              ))}
            </Section>

            <Section title={`Reviews (${data.reviews.length})`} icon={Star}>
              {data.reviews.length === 0 ? <Empty>No reviews</Empty> : data.reviews.map(r => (
                <div key={r.id} className="py-2 border-b border-[#1A1A1A] last:border-0 text-sm">
                  <div className="flex justify-between items-center">
                    <span className="font-mono text-[#39FF14]">{"★".repeat(r.rating)}{"☆".repeat(5 - r.rating)}</span>
                    <span className="font-mono text-[10px] text-[#A1A1A1]">{fmtDate(r.created_at)}</span>
                  </div>
                  {r.title && <p className="text-[#F2F2F2] font-bold mt-1">{r.title}</p>}
                  {r.body && <p className="text-[#A1A1A1] text-xs mt-1">{r.body}</p>}
                </div>
              ))}
            </Section>

            <Section title={`Current Cart (${data.cart_items.length})`} icon={ShoppingCart}>
              {data.cart_items.length === 0 ? <Empty>Cart is empty</Empty> : (
                <div>
                  {data.cart_items.map((it, idx) => (
                    <div key={idx} className="flex justify-between items-center py-2 border-b border-[#1A1A1A] last:border-0 text-sm" data-testid={`detail-cart-item-${idx}`}>
                      <div>
                        <p className="text-[#F2F2F2]">{it.name}{it.selected_color && <span className="text-[#39FF14] text-xs"> ({it.selected_color})</span>}</p>
                        <p className="font-mono text-[10px] text-[#A1A1A1]">qty {it.quantity} × ${Number(it.price || 0).toFixed(2)}</p>
                      </div>
                      <p className="font-mono text-[#39FF14] font-bold">${(Number(it.price || 0) * Number(it.quantity || 0)).toFixed(2)}</p>
                    </div>
                  ))}
                </div>
              )}
            </Section>

            <Section title={`Cart Activity (${data.cart_events.length})`} icon={ShoppingCart}>
              {data.cart_events.length === 0 ? <Empty>No cart activity yet</Empty> : (
                <div className="max-h-[420px] overflow-y-auto">
                  {data.cart_events.map(ev => {
                    const action = ev.action;
                    const isAdd = action === "added";
                    const isRemove = action === "removed";
                    const isQty = action === "quantity_changed";
                    const Icon = isRemove ? Minus : Plus;
                    const tone = isAdd ? "text-[#39FF14]" : isRemove ? "text-red-400" : "text-yellow-400";
                    const label = isAdd ? "ADDED" : isRemove ? "REMOVED" : "QTY CHANGED";
                    return (
                      <div key={ev.id} className="flex items-start gap-3 py-2 border-b border-[#1A1A1A] last:border-0 text-sm" data-testid={`detail-cart-event-${ev.id}`}>
                        <Icon className={`h-3 w-3 mt-1 shrink-0 ${tone}`} strokeWidth={2.5} />
                        <div className="flex-1 min-w-0">
                          <div className="flex justify-between items-baseline gap-2 flex-wrap">
                            <span className={`font-mono text-[10px] uppercase tracking-[0.15em] font-bold ${tone}`}>{label}</span>
                            <span className="font-mono text-[10px] text-[#A1A1A1]">{fmtDateTime(ev.created_at)}</span>
                          </div>
                          <p className="text-[#F2F2F2] truncate">
                            {ev.product_name || ev.product_id}
                            {ev.selected_color && <span className="text-[#39FF14] text-xs"> ({ev.selected_color})</span>}
                          </p>
                          <p className="font-mono text-[10px] text-[#A1A1A1]">
                            {isQty
                              ? `quantity → ${ev.quantity_new} (${ev.quantity_delta > 0 ? "+" : ""}${ev.quantity_delta})`
                              : `qty ${Math.abs(ev.quantity_delta || 0)} · $${Number(ev.price || 0).toFixed(2)}`}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </Section>

            <Section title={`IP Addresses (${data.ips.length})`} icon={Globe}>
              {data.ips.length === 0 ? <Empty>No IPs recorded</Empty> : data.ips.map(rec => (
                <div key={rec.ip} className="py-2 border-b border-[#1A1A1A] last:border-0 text-sm" data-testid={`detail-ip-${rec.ip}`}>
                  <div className="flex justify-between items-start gap-3 flex-wrap">
                    <div className="font-mono">
                      <span className="text-[#F2F2F2] font-bold">{rec.ip}</span>
                      {rec.geo?.flag && <span className="ml-2">{rec.geo.flag}</span>}
                      {(rec.geo?.city || rec.geo?.country) && (
                        <span className="ml-2 text-[#A1A1A1] text-xs">
                          {[rec.geo.city, rec.geo.region, rec.geo.country].filter(Boolean).join(", ")}
                        </span>
                      )}
                    </div>
                    <div className="text-right">
                      <p className="font-mono text-[#39FF14] font-bold text-xs">{rec.visit_count} visit{rec.visit_count === 1 ? "" : "s"}</p>
                      <p className="font-mono text-[10px] text-[#A1A1A1]">last {fmtDateTime(rec.last_seen)}</p>
                    </div>
                  </div>
                </div>
              ))}
            </Section>

            <Section title={`Site Visit History (${data.visit_count_total}${data.visits.length < data.visit_count_total ? `, showing latest ${data.visits.length}` : ""})`} icon={Eye}>
              {data.visits.length === 0 ? <Empty>No visit history</Empty> : (
                <div className="max-h-[420px] overflow-y-auto">
                  <table className="w-full text-xs font-mono">
                    <thead className="sticky top-0 bg-[#050505]">
                      <tr className="border-b border-[#1A1A1A]">
                        <th className="px-2 py-2 text-left text-[10px] text-[#39FF14] uppercase tracking-wider">When</th>
                        <th className="px-2 py-2 text-left text-[10px] text-[#39FF14] uppercase tracking-wider">Path</th>
                        <th className="px-2 py-2 text-left text-[10px] text-[#39FF14] uppercase tracking-wider">IP</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.visits.map(v => (
                        <tr key={v.id} className="border-b border-[#1A1A1A] last:border-b-0 hover:bg-[#141414]" data-testid={`detail-visit-${v.id}`}>
                          <td className="px-2 py-2 text-[#A1A1A1] whitespace-nowrap">{fmtDateTime(v.created_at)}</td>
                          <td className="px-2 py-2 text-[#F2F2F2] truncate max-w-[200px]" title={v.path}>{v.path}</td>
                          <td className="px-2 py-2 text-[#A1A1A1]">{v.ip || "—"}{v.geo?.flag && <span className="ml-1">{v.geo.flag}</span>}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Section>
          </div>
        )}
      </div>
    </div>
  );
}

const Section = ({ title, icon: Icon, children }) => (
  <div>
    <div className="flex items-center gap-2 mb-2">
      <Icon className="h-4 w-4 text-[#39FF14]" strokeWidth={1.5} />
      <p className="font-mono uppercase tracking-[0.15em] text-[10px] font-bold text-[#A1A1A1]">{title}</p>
    </div>
    <div className="bg-[#050505] border border-[#1A1A1A] px-3">{children}</div>
  </div>
);
const Empty = ({ children }) => <p className="text-[#A1A1A1] font-mono text-xs py-3">{children}</p>;

export default function AdminUsersPage() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { id: routeUserId } = useParams();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [verifiedFilter, setVerifiedFilter] = useState("all");
  const [activeUserId, setActiveUserId] = useState(routeUserId || null);

  const fetchUsers = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/admin/users`);
      setUsers(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (authLoading) return;
    if (!user || user.role !== "admin") { navigate("/"); return; }
    fetchUsers();
  }, [user, authLoading, navigate, fetchUsers]);

  useEffect(() => { setActiveUserId(routeUserId || null); }, [routeUserId]);

  const filtered = users.filter(u => {
    if (roleFilter !== "all" && u.role !== roleFilter) return false;
    if (verifiedFilter === "verified" && !u.email_verified) return false;
    if (verifiedFilter === "unverified" && u.email_verified) return false;
    if (search) {
      const q = search.toLowerCase();
      if (!u.email.toLowerCase().includes(q) && !(u.name || "").toLowerCase().includes(q)) return false;
    }
    return true;
  });

  const totalRevenue = users.reduce((acc, u) => acc + (u.total_spent || 0), 0);

  // Aggregate referral sources
  const referralBreakdown = users.reduce((acc, u) => {
    const src = u.referral_source || "(not specified)";
    acc[src] = (acc[src] || 0) + 1;
    return acc;
  }, {});
  const referralRows = Object.entries(referralBreakdown)
    .sort((a, b) => b[1] - a[1])
    .filter(([src]) => src !== "(not specified)");
  const unspecifiedCount = referralBreakdown["(not specified)"] || 0;
  const knownSourceTotal = users.length - unspecifiedCount;

  if (loading) return <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: "#050505" }}><div className="w-8 h-8 border border-[#39FF14] border-t-transparent animate-spin" /></div>;

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#050505" }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <button onClick={() => navigate("/admin")} data-testid="back-to-admin" className="flex items-center text-[#A1A1A1] hover:text-[#39FF14] font-mono text-xs uppercase tracking-wider mb-3">
          <ArrowLeft className="h-3 w-3 mr-2" /> Back to Admin
        </button>

        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-end gap-4 mb-8">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Users className="h-5 w-5 text-[#39FF14]" strokeWidth={1.5} />
              <p className="font-mono uppercase tracking-[0.3em] text-xs font-bold text-[#39FF14]">Admin</p>
            </div>
            <h1 data-testid="users-heading" className="text-4xl sm:text-5xl font-black uppercase tracking-tighter text-[#F2F2F2]" style={{ fontFamily: "Chivo, sans-serif" }}>
              Registered Users
            </h1>
            <p className="text-[#A1A1A1] text-sm mt-2">Click any row to view full activity history.</p>
          </div>
        </div>

        {/* Stat tiles */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-[1px] bg-[#1A1A1A] mb-4">
          <Tile label="Total Users" value={users.length} testid="stat-users-total" />
          <Tile label="Verified" value={users.filter(u => u.email_verified).length} testid="stat-users-verified" />
          <Tile label="With Orders" value={users.filter(u => u.order_count > 0).length} testid="stat-users-buyers" />
          <Tile label="Lifetime Revenue" value={`$${totalRevenue.toFixed(2)}`} testid="stat-users-revenue" />
        </div>

        {/* Referral source breakdown */}
        <div className="bg-[#0D0D0D] border border-[#1A1A1A] p-5 mb-8" data-testid="referral-breakdown">
          <div className="flex items-center justify-between mb-3">
            <p className="font-mono uppercase tracking-[0.15em] text-[10px] font-bold text-[#39FF14]">Where Customers Heard About Us</p>
            <p className="font-mono text-[10px] text-[#A1A1A1]">
              {knownSourceTotal} answered · {unspecifiedCount} skipped
            </p>
          </div>
          {referralRows.length === 0 ? (
            <p className="text-[#A1A1A1] text-xs font-mono">No referral data yet — new registrations will populate this.</p>
          ) : (
            <div className="space-y-1.5">
              {referralRows.map(([src, count]) => {
                const pct = knownSourceTotal > 0 ? (count / knownSourceTotal) * 100 : 0;
                return (
                  <div key={src} className="flex items-center gap-3" data-testid={`referral-row-${src}`}>
                    <p className="font-mono text-xs text-[#F2F2F2] w-44 shrink-0 truncate">{src}</p>
                    <div className="flex-1 h-5 bg-[#050505] border border-[#1A1A1A] relative overflow-hidden">
                      <div className="h-full bg-[#39FF14]/30 border-r border-[#39FF14]" style={{ width: `${pct}%` }} />
                    </div>
                    <p className="font-mono text-xs text-[#39FF14] font-bold w-20 text-right shrink-0">{count} ({pct.toFixed(0)}%)</p>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3 mb-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#A1A1A1]" />
            <Input
              data-testid="users-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search email or name…"
              className="pl-10 bg-[#0D0D0D] border-[#1A1A1A] text-[#F2F2F2] rounded-none focus:border-[#39FF14] font-mono"
            />
          </div>
          <select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)} data-testid="users-role-filter"
            className="bg-[#0D0D0D] border border-[#1A1A1A] text-[#F2F2F2] focus:border-[#39FF14] outline-none px-3 font-mono text-sm uppercase tracking-wider">
            <option value="all">All Roles</option>
            <option value="customer">Customer</option>
            <option value="admin">Admin</option>
          </select>
          <select value={verifiedFilter} onChange={(e) => setVerifiedFilter(e.target.value)} data-testid="users-verified-filter"
            className="bg-[#0D0D0D] border border-[#1A1A1A] text-[#F2F2F2] focus:border-[#39FF14] outline-none px-3 font-mono text-sm uppercase tracking-wider">
            <option value="all">All Verified</option>
            <option value="verified">Verified</option>
            <option value="unverified">Unverified</option>
          </select>
        </div>

        {/* Table */}
        <div className="bg-[#0D0D0D] border border-[#1A1A1A] overflow-x-auto" data-testid="users-table">
          <table className="w-full text-sm min-w-[900px]">
            <thead>
              <tr className="border-b border-[#1A1A1A]">
                <Th>User</Th>
                <Th>Source</Th>
                <Th>Role</Th>
                <Th>Verified</Th>
                <Th align="right">Orders</Th>
                <Th align="right">Spent</Th>
                <Th align="right">Preorders</Th>
                <Th align="right">Waitlist</Th>
                <Th>Joined</Th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={9} className="text-center py-10 text-[#A1A1A1] font-mono text-xs">No users match filters.</td></tr>
              )}
              {filtered.map(u => (
                <tr key={u.id} data-testid={`user-row-${u.id}`} onClick={() => navigate(`/admin/users/${u.id}`)}
                  className="border-b border-[#1A1A1A] last:border-0 hover:bg-[#141414] cursor-pointer transition-colors">
                  <td className="px-3 py-3">
                    <p className="text-[#F2F2F2] font-bold truncate max-w-[260px]">{u.name || "(no name)"}</p>
                    <p className="font-mono text-[10px] text-[#A1A1A1] truncate max-w-[260px]">{u.email}</p>
                  </td>
                  <td className="px-3 py-3 font-mono text-[10px] text-[#A1A1A1] whitespace-nowrap">{u.referral_source || "—"}</td>
                  <td className="px-3 py-3">
                    <span className={`px-2 py-0.5 font-mono text-[9px] uppercase tracking-wider ${u.role === "admin" ? "bg-[#39FF14] text-[#050505]" : "bg-[#1A1A1A] text-[#A1A1A1]"}`}>{u.role}</span>
                  </td>
                  <td className="px-3 py-3">
                    {u.email_verified
                      ? <ShieldCheck className="h-4 w-4 text-[#39FF14]" strokeWidth={1.5} />
                      : <Mail className="h-4 w-4 text-yellow-400" strokeWidth={1.5} />}
                  </td>
                  <td className="px-3 py-3 text-right font-mono text-[#F2F2F2]">{u.order_count}</td>
                  <td className="px-3 py-3 text-right font-mono text-[#39FF14] font-bold">${u.total_spent.toFixed(2)}</td>
                  <td className="px-3 py-3 text-right font-mono text-[#F2F2F2]">{u.preorder_count}</td>
                  <td className="px-3 py-3 text-right font-mono text-[#F2F2F2]">{u.waitlist_count}</td>
                  <td className="px-3 py-3 font-mono text-[10px] text-[#A1A1A1] whitespace-nowrap">{fmtDate(u.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {activeUserId && (
          <UserDetail userId={activeUserId} onClose={() => navigate("/admin/users")} />
        )}
      </div>
    </div>
  );
}

const Th = ({ children, align = "left" }) => (
  <th className={`px-3 py-2 text-${align} font-mono text-[10px] uppercase tracking-[0.15em] font-bold text-[#39FF14]`}>{children}</th>
);

const Tile = ({ label, value, testid }) => (
  <div className="bg-[#0D0D0D] p-5" data-testid={testid}>
    <p className="font-mono uppercase tracking-[0.15em] text-[10px] font-bold text-[#A1A1A1] mb-2">{label}</p>
    <p className="text-2xl sm:text-3xl font-black text-[#F2F2F2]" style={{ fontFamily: "Chivo, sans-serif" }}>{value}</p>
  </div>
);
